import { addDays, fromYmd, ymd } from "./dates.js";

export type PublicHoliday = {
  date: string;
  name: string;
};

/** Gregorian Easter Sunday (Meeus/Jones/Butcher), returned as a zoneless date. */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function nthWeekday(year: number, month: number, weekday: number, nth: number): Date {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (weekday - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, month, 1 + offset + 7 * (nth - 1)));
}

/**
 * Years NSW has specifically declared an extra day for Anzac Day.
 *
 * Exported so a test can assert we have not run past the end of what anybody
 * actually looked up. Extend it by checking the NSW public holidays page, not by
 * inferring a pattern — there isn't one.
 */
export const ANZAC_EXTRA_DAY_YEARS: readonly number[] = [2026, 2027];

/**
 * NSW statewide public holidays.
 *
 * The August Bank Holiday is deliberately excluded: the NSW Government says it
 * is not a declared public holiday. Local and regional holidays are also outside
 * this statewide calendar.
 */
export function nswPublicHolidays(year: number): PublicHoliday[] {
  const holidays = new Map<string, string>();
  const add = (date: Date, name: string) => holidays.set(ymd(date), name);

  const newYear = new Date(Date.UTC(year, 0, 1));
  add(newYear, "New Year's Day");
  if (newYear.getUTCDay() === 6) add(addDays(newYear, 2), "New Year's Day (additional day)");
  if (newYear.getUTCDay() === 0) add(addDays(newYear, 1), "New Year's Day (additional day)");

  const australiaDay = new Date(Date.UTC(year, 0, 26));
  add(
    australiaDay.getUTCDay() === 6
      ? addDays(australiaDay, 2)
      : australiaDay.getUTCDay() === 0
        ? addDays(australiaDay, 1)
        : australiaDay,
    "Australia Day"
  );

  const easter = easterSunday(year);
  add(addDays(easter, -2), "Good Friday");
  add(addDays(easter, -1), "Easter Saturday");
  add(easter, "Easter Sunday");
  add(addDays(easter, 1), "Easter Monday");

  const anzac = new Date(Date.UTC(year, 3, 25));
  add(anzac, "Anzac Day");
  // Unlike every other rule in this file, this one cannot be derived: NSW does
  // not grant Anzac Day a substitute day as a standing rule, it declares one for
  // particular years. So it is data, and data with an end.
  //
  // `holidays.test.ts` fails once the current year reaches the end of this range,
  // which is the only thing that will make anyone go and look the next years up.
  // Until then the seed would quietly suggest 8h on a declared public holiday.
  if (ANZAC_EXTRA_DAY_YEARS.includes(year)) {
    add(
      addDays(anzac, anzac.getUTCDay() === 6 ? 2 : 1),
      "Anzac Day (additional day)"
    );
  }

  add(nthWeekday(year, 5, 1, 2), "King's Birthday");
  add(nthWeekday(year, 9, 1, 1), "Labour Day");

  const christmas = new Date(Date.UTC(year, 11, 25));
  const boxingDay = new Date(Date.UTC(year, 11, 26));
  add(christmas, "Christmas Day");
  add(boxingDay, "Boxing Day");

  // Weekend Christmas/Boxing holidays receive additional weekdays. Allocate
  // the next unused weekday in order, matching the NSW statutory calendar.
  for (const [holiday, name] of [
    [christmas, "Christmas Day"],
    [boxingDay, "Boxing Day"],
  ] as const) {
    if (holiday.getUTCDay() !== 0 && holiday.getUTCDay() !== 6) continue;
    let observed = addDays(holiday, 1);
    while (
      observed.getUTCDay() === 0 ||
      observed.getUTCDay() === 6 ||
      holidays.has(ymd(observed))
    ) {
      observed = addDays(observed, 1);
    }
    add(observed, `${name} (additional day)`);
  }

  return [...holidays.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, name]) => ({ date, name }));
}

export function nswPublicHoliday(date: Date | string): PublicHoliday | null {
  const key = typeof date === "string" ? date : ymd(date);
  const year = fromYmd(key).getUTCFullYear();
  return nswPublicHolidays(year).find((holiday) => holiday.date === key) ?? null;
}
